import CircularProgress from '@material-ui/core/CircularProgress';
import Tooltip from '@material-ui/core/Tooltip';
import { Typography } from '@material-ui/core';
import DialogActions from '@material-ui/core/DialogActions';
import WarningIcon from '@material-ui/icons/Warning';
import InputAdornment from '@material-ui/core/InputAdornment';
import { BallBeat } from 'react-pure-loaders';
import ClickAwayListener from '@material-ui/core/ClickAwayListener';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import List from '@material-ui/core/List';
import ListSubheader from '@material-ui/core/ListSubheader';
import { withStyles } from '@material-ui/core/styles';
import PropTypes from 'prop-types';
import React from 'react';
import { hotkeys } from 'react-keyboard-shortcuts';
import Deprecated from '../core/Deprecated';
import DialogMultiActions from '../core/DialogMultiActions';
import Errored from '../core/Errored';
import ListResult from '../core/ListResult';
import PrimaryAddButton from '../core/PrimaryAddButton';
import PrimaryRepeatButton from '../core/PrimaryRepeatButton';
import { globalStyles } from '../core/style/gstheme';
import TitledTextField from '../core/TitledTextField';
import { getGraphFromProject, isInOntology, makeCancelable, sanitiseText, sortObjects } from '../core/utils/utils';
import GS from '../grafsync/grafsync';
import { MAX_CLASSNAME_LENGTH } from './constants';
import OntologyLink from './OntologyLink';
import IRIentry from '../core/IRIentry';

const styles = Object.assign({}, globalStyles);

const iriAliasMapping = {
    "http://www.w3.org/2002/07/owl#Class": "OwlClass",
    "http://www.w3.org/2002/07/owl#DatatypeProperty": "DatatypeProperty",
    "http://www.w3.org/2002/07/owl#ObjectProperty": "ObjectProperty",
    "http://www.w3.org/2002/07/owl#AnnotationProperty": "AnnotationProperty"
};

const aliasLabels = {
    "OwlClass": "Class",
    "ObjectProperty": "Object property",
    "DatatypeProperty": "Datatype property",
    "AnnotationProperty": "Annotation property"
};

class CreateResourceDialog extends React.Component {

    constructor(props) {
        super(props);
        this.state = {
            open: false,
            acOpen: false,
            checking: false,
            checked: false,
            processingIRI: false,
            isDuplicate: false,
            label: "",
            addedResources: [],
            resourceIRI: "",
            computedIRI: "",
            uniqueIRI: false,
            viewIRI: false,
            iriRejected: false,
            isErrored: null
        };
        this.cancellable = null;
    }

    hot_keys = {
        'alt+n': {
            priority: 1,
            handler: () => this.handleClickOpen()
        }
    };

    handleClickOpen = () => {
        this.setState({
            open: true, acOpen: false
        });
        if (this.props.onOpen) this.props.onOpen();
    };

    findResource = (value, lang) => {
        let excludeByPath = this.props.excludeByPath && this.props.source ?
            this.props.excludeByPath(this.props.source)
            :
            null;
        let searchParts = [{
            predicate: "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
            value: this.props.searchClassFilter
        },
        {
            predicate: "http://www.w3.org/2000/01/rdf-schema#label",
            value: value,
            isExactMatch: false
        }];
        this.doAutocomplete(
            {
                search: searchParts,
                language: lang,
                exclude: this.props.exclude,
                includeDeleted: true,
                ...(excludeByPath) && { excludeByPath },
                graph: [getGraphFromProject(this.props.projectIRI)],
                predicates: [
                    "http://www.w3.org/2000/01/rdf-schema#label",
                    "http://www.w3.org/2000/01/rdf-schema#isDefinedBy",
                    "https://grafsync.graphifi.com/schema/gs/visibility",
                    "http://www.w3.org/1999/02/22-rdf-syntax-ns#type",
                    "http://www.w3.org/2002/07/owl#deprecated",
                    "https://grafsync.graphifi.com/schema/gs/pendingDeletion"
                ]
            })
            .promise
            .then((data) => {
                const sortedResults = data.results && data.results.length > 0 ? sortObjects([...data.results], 'label', lang, null, false).slice(0, 5) : null;
                this.setState({
                    results: sortedResults,
                    acOpen: data.results && data.results.length > 0 ? true : false
                });
                const isDuplicate = data.results && data.results.length > 0 ? data.results.some(result => {
                    // We may get an edge case of mutiple prefLabels in a single language
                    if (Array.isArray(result.label[lang])) {
                        return result.label[lang].some(label => label.toLowerCase() === this.state.label.toLowerCase());
                    }
                    else return result.label[lang].toLowerCase() === this.state.label.toLowerCase();
                }) : false;
                this.setState({ isDuplicate: isDuplicate, checked: true, checking: false });
            })
            .catch((e) => {
                if (this.cancellable && !this.cancellable.isCancelled()) this.setState({ isErrored: e });
            });
    };

    // We keep a record of added labels to add to duplicate test because quickly added may not have reached DB
    updateAddedResources = () => {
        this.setState((prevState) => {
            const ARs = [...prevState.addedResources];
            ARs.push(prevState.label);
            return { addedResources: ARs };
        });
    };

    addExistingNode = (iri) => {
        this.setState({ open: false, label: "", acOpen: false });
        this.props.onAddExistingNode(this.props.parentIRI, iri);
        this.props.onClose && this.props.onClose();
    };

    reserveIDs = async () => {
        this.setState({ processingIRI: true });
        // Make IRI reservation call
        const isFound = await GS.reserveIRI(this.state.resourceIRI, this.props.graph, this.props.searchClass);
        const exists = isFound.exists;
        if (exists) {
            this.rejectIRI(true);
            return false;
        }
        const reservationID = isFound.id;
        return {
            reservationID
        };
    };

    loadIDs() {
        this.cancellable = makeCancelable(this.reserveIDs());
        return this.cancellable;
    }

    addResource = async () => {
        try {
            const ids = await this.loadIDs().promise;
            if (this.cancellable && this.cancellable.isCancelled()) return true;
            if (ids !== false) {
                this.setState({ open: false, acOpen: false }, async () => {
                    this.processResource(ids, true);
                });
            }
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    addResourcePlus = async () => {
        try {
            const ids = await this.loadIDs().promise;
            if (this.cancellable && this.cancellable.isCancelled()) return true;
            if (ids !== false) {
                this.processResource(ids);
                this.textInput && this.textInput.current && this.textInput.current.focus();
            }
        }
        catch (e) {
            if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
        }
    };

    processResource = (ids, close = false) => {
        // We need to make sure we have the parent loaded so narrower events happens
        const { reservationID } = ids;
        this.updateAddedResources();
        const { parentIRI } = this.props;
        const { label, resourceIRI } = this.state;
        this.setState({
            label: "",
            computedIRI: "",
            resourceIRI: "",
            processingIRI: false,
            uniqueIRI: false,
            iriRejected: false,
            viewIRI: false,
            acOpen: false
        }, () => {
            if (close) {
                this.props.onClose && this.props.onClose();
            }
            setTimeout(() => {
                this.props.onAddNewNode(parentIRI, resourceIRI, reservationID, label.trim());
            }, 10);
        });
    };

    exitAddResource = () => {
        this.setState({
            open: false,
            label: "",
            computedIRI: "",
            resourceIRI: "",
            acOpen: false,
            processingIRI: false,
            uniqueIRI: false,
            iriRejected: false,
            viewIRI: false
        });
        this.props.onClose && this.props.onClose();
    };

    handleChange = (lang) => (event) => {
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
        const t = event.target;
        const cleanText = sanitiseText(t.value.trimLeft());
        this.setState({ label: cleanText, checking: true, checked: false });
        if (cleanText.length > 0) {
            try {
                if (!this.props.useUuidIRI || this.state.computedIRI === "") {
                    const computedIRI = GS.computeIRI(this.props.ontologyIRI, cleanText, this.props.useSlashIRI, this.props.useUuidIRI, this.props.iriOptions);
                    this.setState({ computedIRI });
                }
            }
            catch (e) {
                if (!this.cancellable || (this.cancellable && !this.cancellable.isCancelled())) this.setState({ isErrored: e });
                return;
            }
            this.hcTimeout = setTimeout(() => {
                if (this.state.addedResources.includes(cleanText)) {
                    this.setState({ isDuplicate: true, checking: false, checked: true });
                    this.handleClickAway();
                }
                else this.findResource(cleanText, lang);
            }, 250);
        }
        else {
            this.handleClickAway();
            this.setState({ computedIRI: "", checking: false, checked: true, isDuplicate: false });
        }
    };

    resetIRIconfirmation = () => this.setState({ uniqueIRI: false, iriRejected: false });

    confirmUniqueIRI = (iri) => this.setState({ uniqueIRI: true, resourceIRI: iri, iriRejected: false });

    rejectIRI = () => this.setState({ uniqueIRI: false, iriRejected: true, processingIRI: false });

    doAutocomplete(payload) {
        this.cancellable = makeCancelable(GS.autocomplete(payload));
        return this.cancellable;
    }

    componentWillUnmount() {
        if (this.cancellable) this.cancellable.cancel();
        if (this.hcTimeout) clearTimeout(this.hcTimeout);
    }

    handleClickAway = () => this.setState({ acOpen: false });

    dialogClick = () => this.setState({ acOpen: false });

    render() {

        const { label, acOpen, isErrored, results, isDuplicate, addedResources, checking, processingIRI,
            checked, computedIRI, uniqueIRI, iriRejected } = this.state;
        const { classes, button, defaultLang, title, placeholder, disableMultiple, useSlashIRI, useUuidIRI,
            searchClass, existing, ontologyIRI, graph, onlyComponentResources } = this.props;

        if (isErrored) return <Errored data-test="resource-error" data={isErrored} />;

        const invalid = label.length === 0 || checking || !checked || !uniqueIRI;

        return (
            <React.Fragment>
                <span onClick={this.handleClickOpen}>{button}</span>
                {this.state.open &&
                    <Dialog
                        onClick={this.dialogClick}
                        data-test="create-resource"
                        maxWidth="md"
                        fullWidth
                        classes={{
                            paper: classes.paper,
                            paperWidthMd: classes.largeDialog
                        }}
                        className={classes.dialog}
                        open={this.state.open}
                        onClose={this.exitAddResource}
                        aria-labelledby="create-resource-title">
                        <DialogTitle
                            data-test="create-resource-title"
                            disableTypography
                            className={classes.mainHeading}
                            id="create-resource-title">{title || 'Create a New Resource'}</DialogTitle>
                        <DialogContent style={{ height: '240px' }}>
                            <form autoComplete="off" noValidate="novalidate">
                                <TitledTextField
                                    dataTest="label"
                                    name="label"
                                    title={"Label"}
                                    autoFocus={true}
                                    required={true}
                                    text={label}
                                    adornment={
                                        checking ?
                                            <Tooltip title="Searching for results">
                                                <CircularProgress data-test="searching-for" color="secondary" size={20} />
                                            </Tooltip>
                                            :
                                            iriRejected ?
                                                <InputAdornment position="end">
                                                    <Tooltip title="There is an IRI clash">
                                                        <WarningIcon data-test="warning" />
                                                    </Tooltip>
                                                </InputAdornment>
                                                :
                                                null
                                    }
                                    placeholder={placeholder || "Label"}
                                    maxLength={MAX_CLASSNAME_LENGTH}
                                    lang={defaultLang}
                                    helperText={
                                        addedResources.includes(label) ? "You added this value recently." : ""
                                    }
                                    onEnter={!isDuplicate && checked ? this.addResource : null}
                                    onCtrlEnter={!isDuplicate && checked ? this.addResourcePlus : null}
                                    onChange={this.handleChange(defaultLang)} />
                                {acOpen &&
                                    <ClickAwayListener onClickAway={this.handleClickAway}>
                                        <List
                                            data-test="list-results"
                                            dense
                                            className={classes.existingResults}
                                            subheader={<ListSubheader className={classes.existingResultsHeader}>Existing Resources</ListSubheader>}
                                            style={{ marginTop: -8 }}>
                                            {results && results.map((result, i) => {
                                                const ontology = result.visibility || result.isDefinedBy;
                                                let label = result.label && result.label[defaultLang] ?
                                                    (
                                                        Array.isArray(result.label[defaultLang]) ?
                                                            result.label[defaultLang].sort().join(" AND ")
                                                            :
                                                            result.label[defaultLang]
                                                    )
                                                    :
                                                    "No label available";
                                                const resultTypes = Array.isArray(result.type) ? result.type : [result.type];
                                                let additionalLabel = "";
                                                const aliasLabel = aliasLabels[resultTypes.find(resultType => aliasLabels.hasOwnProperty(resultType))];
                                                const isIn = isInOntology(result.id, ontology, ontologyIRI);
                                                if (ontology && !isIn) {
                                                    additionalLabel = <div>
                                                        <div>
                                                            {aliasLabel}
                                                        </div>
                                                        <OntologyLink
                                                            key={ontology}
                                                            id={ontology}
                                                            graph={graph}
                                                            handleNotAvailable={true}
                                                            defaultLang={defaultLang} />
                                                    </div>;
                                                }
                                                else additionalLabel = aliasLabel;
                                                const searchClassAlias = iriAliasMapping[searchClass];
                                                const isCorrectClass = resultTypes.some(resultType => resultType === searchClassAlias);
                                                const invalid = (onlyComponentResources && !isIn) || !isCorrectClass || result.invalid || (existing && existing.includes(result.id)) || result.deprecated;
                                                return <ListResult
                                                    key={i}
                                                    text={label}
                                                    iri={result.id}
                                                    pendingDeletion={result.pendingDeletion}
                                                    listSecondIcon={
                                                        result.deprecated ?
                                                            <Deprecated />
                                                            :
                                                            null
                                                    }
                                                    invalid={invalid}
                                                    onClick={!invalid ? () => this.addExistingNode(result.id) : null}
                                                    additionalLabel={additionalLabel} />;
                                            }
                                            )}
                                        </List>
                                    </ClickAwayListener>
                                }
                                <IRIentry
                                    key={computedIRI}
                                    startIRI={computedIRI}
                                    useSlashIRI={useSlashIRI}
                                    graph={graph}
                                    resetIRIconfirmation={this.resetIRIconfirmation}
                                    confirmUniqueIRI={this.confirmUniqueIRI}
                                    useUuidIRI={useUuidIRI}
                                    changeIRI={() => { }}
                                />
                            </form>
                        </DialogContent>
                        {processingIRI ?
                            <DialogActions className={classes.dialogActions}>
                                <div style={{ display: 'flex', height: 40 }}>
                                    <Typography className={classes.subMainHading}>Processing ...</Typography>
                                    <span style={{ marginLeft: 24 }}>
                                        <BallBeat color='#97d700' loading={true} />
                                    </span>
                                </div>
                            </DialogActions>
                            :
                            <DialogMultiActions onCancel={this.exitAddResource}>
                                <PrimaryAddButton title="Add" disabled={invalid} onClick={this.addResource} />
                                {!disableMultiple &&
                                    <PrimaryRepeatButton
                                        dataTest="add-multiple-resources"
                                        title="Add and create another (Ctrl+Enter)"
                                        disabled={invalid}
                                        onClick={this.addResourcePlus} />
                                }
                            </DialogMultiActions>
                        }
                    </Dialog>
                }
            </React.Fragment>
        );
    }
}

CreateResourceDialog.defaultProps = {
    onlyComponentResources: false
};

CreateResourceDialog.propTypes = {
    classes: PropTypes.object,
    onOpen: PropTypes.func,
    source: PropTypes.string,
    onClose: PropTypes.func,
    useSlashIRI: PropTypes.bool,
    useUuidIRI: PropTypes.bool,
    graph: PropTypes.string.isRequired,
    searchClass: PropTypes.string,
    searchClassFilter: PropTypes.array.isRequired,
    parentIRI: PropTypes.string.isRequired,
    ontologyIRI: PropTypes.string.isRequired,
    projectIRI: PropTypes.string.isRequired,
    disableMultiple: PropTypes.bool,
    existing: PropTypes.array,
    button: PropTypes.object,
    defaultLang: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    exclude: PropTypes.array,
    title: PropTypes.string,
    excludeByPath: PropTypes.func,
    onAddExistingNode: PropTypes.func.isRequired,
    onAddNewNode: PropTypes.func.isRequired,
    onlyComponentResources: PropTypes.bool,
    iriOptions: PropTypes.object
};

export default withStyles(styles)(hotkeys(CreateResourceDialog));
